import { getInstance } from './instance';

const instance = getInstance();

const byDraftUrl = (draftId) => `/ghl/websites/by-draft/${draftId}`;

/**
 * Looks up the DS `ds__ghl_websites` record of a draft and tells the three
 * answers apart, so callers can stop polling on a definitive one:
 *
 * - `{ outcome: 'found', data }`  — 200, the record exists (its
 *   `ghlBookPageUrl` may still be empty).
 * - `{ outcome: 'not-found' }`     — 404, DS has no record for this draft.
 *   DS creates it only when the book builder publishes the draft's site
 *   (`bba.books.site-ready` Kafka event), so a draft that was never
 *   published answers 404. That is an expected answer, not a failure.
 * - `{ outcome: 'error', status }` — anything else (5xx, network, ...).
 *
 * A 404 resolves through `validateStatus` instead of rejecting, so nothing is
 * logged for it. The browser's own network log still lists the 404; no
 * client-side option can hide that line.
 */
export const lookupGhlWebsiteByDraft = (draftId) => {
  const url = byDraftUrl(draftId);
  return instance
    .get(url, {
      validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    })
    .then((response) => {
      if (response.status === 404) return { outcome: 'not-found' };
      return { outcome: 'found', data: response.data };
    })
    .catch((error) => {
      const status = error?.response?.status;
      console.warn(
        `lookupGhlWebsiteByDraft failed [${status ?? 'network'}] ${url}`,
        error?.response?.data ?? error?.message,
      );
      return { outcome: 'error', status };
    });
};

/** Returns the record, or null when there is none or the lookup failed. */
export const getGhlWebsiteByDraft = (draftId) =>
  lookupGhlWebsiteByDraft(draftId).then((result) =>
    result.outcome === 'found' ? result.data : null,
  );
